// frontend/src/SuperAdminDashboard.tsx - Super Admin Dashboard for Cross-Tenant Management

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useAuth } from './AuthContext';
import { API_URL } from './apiConfig';

// Interfaces for our data structures
interface Tenant {
  id: number;
  name: string;
  slug: string;
  contact_email: string | null;
  subscription_tier: string;
  is_active: boolean;
  created_at: string;
  user_count: number;
  process_count: number;
}

interface SuperAdminUser {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  tenant_id: number | null;
  tenant_name: string | null;
  last_login_at: string | null;
  created_at: string;
}

interface SystemStats {
  total_tenants: number;
  active_tenants: number;
  total_users: number;
  active_users: number;
  total_processes: number;
  active_processes: number;
  recent_logins: number;
}

// Interfaces for our form data
interface TenantFormData {
  name: string;
  slug: string;
  contact_email: string;
  subscription_tier: string;
  is_active: boolean;
}

interface UserFormData {
  username: string;
  email: string;
  full_name: string;
  password: string; // Keep password for creation, optional for update
  role: string;
  tenant_id: number | string; // Use string for form, convert to number on submit
  is_active: boolean;
}

interface SuperAdminDashboardProps {
  onBack: () => void;
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ onBack }) => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'tenants' | 'users' | 'audit'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  
  // Data states
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<SuperAdminUser[]>([]);
  const [selectedTenantFilter, setSelectedTenantFilter] = useState<string>('all');
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  
  // Modal and form states
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [editingUser, setEditingUser] = useState<SuperAdminUser | null>(null);
  
  // Form data states
  const [tenantForm, setTenantForm] = useState<TenantFormData>({
    name: '',
    slug: '',
    contact_email: '',
    subscription_tier: 'basic',
    is_active: true,
  });
  
  const [userForm, setUserForm] = useState<UserFormData>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'EVALUATION_PANEL_MEMBER',
    tenant_id: '', // Default to empty string for the select input
    is_active: true,
  });

  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  // Fetch initial data on component mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Fetch data when the active tab changes
  useEffect(() => {
    // Reset forms when switching tabs to avoid carrying over old data
    if(activeTab !== 'tenants') resetTenantForm();
    if(activeTab !== 'users') resetUserForm();

    if (activeTab === 'overview' && !systemStats) {
      loadSystemStats();
    } else if (activeTab === 'tenants') {
      loadTenants();
    } else if (activeTab === 'users') {
      loadUsers();
      // Also load tenants if they haven't been loaded, for the dropdown
      if (tenants.length === 0) {
        loadTenants();
      }
    } else if (activeTab === 'audit') {
      loadAuditLogs();
    }
  }, [activeTab]);

  // Function to show a notification message
  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Helper to get authorization headers for API calls
  const getAuthHeaders = () => {
    const token = Cookies.get('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  // --- DATA LOADING FUNCTIONS ---
  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      await loadSystemStats();
    } catch (error) {
      console.error('Error loading initial data:', error);
      showNotification('error', 'Failed to load system data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSystemStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/super-admin/stats`, { headers: getAuthHeaders() });
      setSystemStats(response.data);
    } catch (error) {
      console.error('Error loading system stats:', error);
      showNotification('error', 'Failed to load system statistics');
    }
  };

  const loadTenants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/super-admin/tenants`, { headers: getAuthHeaders() });
      setTenants(response.data);
    } catch (error) {
      console.error('Error loading tenants:', error);
      showNotification('error', 'Failed to load tenants');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/super-admin/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification('error', 'Failed to load users');
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/super-admin/audit-logs?limit=50`, { headers: getAuthHeaders() });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      showNotification('error', 'Failed to load audit logs');
    }
  };

  // --- FORM HANDLING AND API CALLS ---

  // Handle Tenant Create/Update
  const handleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = editingTenant 
      ? `${API_URL}/api/super-admin/tenants/${editingTenant.id}`
      : `${API_URL}/api/super-admin/tenants`;
    const method = editingTenant ? 'put' : 'post';
    
    try {
      const response = await axios[method](url, tenantForm, { headers: getAuthHeaders() });
      const message = editingTenant ? 'updated' : 'created';
      showNotification('success', `Tenant "${response.data.name}" ${message} successfully`);
      resetTenantForm();
      loadTenants(); // Refresh the list
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || `Failed to ${editingTenant ? 'update' : 'create'} tenant`;
      showNotification('error', errorMessage);
    }
  };

  // Handle User Create/Update
  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = editingUser
      ? `${API_URL}/api/super-admin/users/${editingUser.id}`
      : `${API_URL}/api/super-admin/users`;
    const method = editingUser ? 'put' : 'post';

    // Prepare payload, don't send password if it's empty during an update
    const payload: any = { ...userForm, tenant_id: userForm.tenant_id || null };
    if (editingUser && !userForm.password) {
      delete payload.password;
    }
    
    try {
      const response = await axios[method](url, payload, { headers: getAuthHeaders() });
      const message = editingUser ? 'updated' : 'created';
      showNotification('success', `User "${response.data.username}" ${message} successfully`);
      resetUserForm();
      loadUsers(); // Refresh the list
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || `Failed to ${editingUser ? 'update' : 'create'} user`;
      showNotification('error', errorMessage);
    }
  };

  // Handle clicking the "Edit" button for a tenant
  const handleEditTenant = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setTenantForm({
      name: tenant.name,
      slug: tenant.slug,
      contact_email: tenant.contact_email || '',
      subscription_tier: tenant.subscription_tier,
      is_active: tenant.is_active,
    });
    setShowTenantModal(true);
  };

  // Handle clicking the "Edit" button for a user
  const handleEditUser = (user: SuperAdminUser) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      password: '', // Clear password for editing
      role: user.role,
      tenant_id: user.tenant_id || '',
      is_active: user.is_active,
    });
    setShowUserModal(true);
  };

  // Handle toggling a user's active status
  const handleToggleUserActive = async (user: SuperAdminUser) => {
    try {
      const payload = { is_active: !user.is_active };
      await axios.put(`${API_URL}/api/super-admin/users/${user.id}`, payload, { headers: getAuthHeaders() });
      showNotification('success', `User ${user.username} has been ${!user.is_active ? 'activated' : 'deactivated'}.`);
      loadUsers(); // Refresh the list
    } catch (error: any) {
      showNotification('error', 'Failed to update user status.');
    }
  };

  // Filter users by tenant
  const filterUsersByTenant = (tenantFilter: string) => {
    setSelectedTenantFilter(tenantFilter);
    if (tenantFilter === 'all') {
      setFilteredUsers(users);
    } else if (tenantFilter === 'system') {
      setFilteredUsers(users.filter(user => !user.tenant_id));
    } else {
      setFilteredUsers(users.filter(user => user.tenant_id?.toString() === tenantFilter));
    }
  };

  // Update filtered users when users data changes
  useEffect(() => {
    filterUsersByTenant(selectedTenantFilter);
  }, [users]);

  // --- FORM RESET FUNCTIONS ---
  const resetTenantForm = () => {
    setTenantForm({ name: '', slug: '', contact_email: '', subscription_tier: 'basic', is_active: true });
    setShowTenantModal(false);
    setEditingTenant(null);
    setFormErrors({});
  };

  const resetUserForm = () => {
    setUserForm({ username: '', email: '', full_name: '', password: '', role: 'EVALUATION_PANEL_MEMBER', tenant_id: '', is_active: true });
    setShowUserModal(false);
    setEditingUser(null);
    setFormErrors({});
  };

  // --- STYLES ---
  const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)',
      padding: 'var(--spacing-lg)'
    },
    contentWrapper: {
      maxWidth: '1400px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: 'var(--font-size-headline-large)',
      fontWeight: 'var(--font-weight-headline)',
      color: 'var(--color-text-on-gradient)',
      marginBottom: 'var(--spacing-xl)',
      textAlign: 'center' as 'center'
    },
    card: {
      backgroundColor: 'var(--color-surface)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--color-surface-variant)',
      marginBottom: 'var(--spacing-lg)'
    },
    tabContainer: {
      display: 'flex',
      borderBottom: '1px solid var(--color-surface-variant)',
      marginBottom: 'var(--spacing-lg)'
    },
    tab: {
      padding: 'var(--spacing-md) var(--spacing-lg)',
      cursor: 'pointer',
      borderBottom: '3px solid transparent',
      transition: 'all 0.2s ease',
      fontWeight: '500'
    },
    activeTab: {
      borderBottomColor: 'var(--color-primary)',
      color: 'var(--color-primary)',
      backgroundColor: 'var(--color-surface-variant)'
    },
    // Modal styles
    modalOverlay: { 
      position: 'fixed' as 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0, 0, 0, 0.6)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      backdropFilter: 'blur(5px)'
    },
    modalContent: { 
      background: 'var(--color-surface)', 
      padding: 'var(--spacing-xl)', 
      borderRadius: 'var(--radius-lg)', 
      boxShadow: 'var(--shadow-xl)', 
      width: '90%', 
      maxWidth: '800px', 
      border: '1px solid var(--color-surface-variant)'
    },
  };

  // --- RENDER LOGIC ---
  if (isLoading) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={styles.contentWrapper}>
          <h1 style={styles.pageTitle}>Super Admin Dashboard</h1>
          <div style={{ textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto'}}>
              <div className="loading-spinner" style={{ margin: '0 auto 1rem auto' }}></div>
              <div style={{ fontSize: 'var(--font-size-lg)' }}>Loading system data...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer} className="density-compact">
      {/* Notification */}
      {notification && (
        <div style={{ 
          position: 'fixed', 
          top: '1rem', 
          right: '1rem', 
          zIndex: 1001, 
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', 
          color: 'white', 
          padding: '1rem 1.5rem', 
          borderRadius: 'var(--radius-lg)', 
          boxShadow: 'var(--shadow-lg)'
        }}>
          <span>{notification.type === 'success' ? '✅' : '❌'}</span> {notification.message}
        </div>
      )}

      {/* Tenant Create/Edit Modal */}
      {showTenantModal && (
        <div style={styles.modalOverlay} onClick={resetTenantForm}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>{editingTenant ? 'Edit Tenant' : 'Create New Tenant'}</h3>
            <form onSubmit={handleTenantSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group"><label className="form-label required">Organisation Name</label><input type="text" value={tenantForm.name} onChange={(e) => setTenantForm(prev => ({ ...prev, name: e.target.value }))} className="input" required /></div>
              <div className="form-group"><label className="form-label required">Slug</label><input type="text" value={tenantForm.slug} onChange={(e) => setTenantForm(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))} className="input" required disabled={!!editingTenant} /></div>
              <div className="form-group"><label className="form-label">Contact Email</label><input type="email" value={tenantForm.contact_email} onChange={(e) => setTenantForm(prev => ({ ...prev, contact_email: e.target.value }))} className="input" /></div>
              <div className="form-group"><label className="form-label">Subscription Tier</label><select value={tenantForm.subscription_tier} onChange={(e) => setTenantForm(prev => ({ ...prev, subscription_tier: e.target.value }))} className="select"><option value="basic">Basic</option><option value="premium">Premium</option><option value="enterprise">Enterprise</option></select></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label" style={{display: 'flex', alignItems: 'center'}}><input type="checkbox" checked={tenantForm.is_active} onChange={(e) => setTenantForm(prev => ({ ...prev, is_active: e.target.checked }))} style={{ marginRight: '0.5rem', height: '1rem', width: '1rem' }}/> Active</label></div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" onClick={resetTenantForm} className="btn btn-tertiary">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingTenant ? 'Update Tenant' : 'Create Tenant'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Create/Edit Modal */}
      {showUserModal && (
        <div style={styles.modalOverlay} onClick={resetUserForm}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>{editingUser ? 'Edit User' : 'Create New User'}</h3>
            <form onSubmit={handleUserSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group"><label className="form-label required">Full Name</label><input type="text" value={userForm.full_name} onChange={(e) => setUserForm(prev => ({ ...prev, full_name: e.target.value }))} className="input" required /></div>
              <div className="form-group"><label className="form-label required">Username</label><input type="text" value={userForm.username} onChange={(e) => setUserForm(prev => ({ ...prev, username: e.target.value.toLowerCase() }))} className="input" disabled={!!editingUser} required /></div>
              <div className="form-group"><label className="form-label required">Email</label><input type="email" value={userForm.email} onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))} className="input" required /></div>
              <div className="form-group"><label className="form-label">Password {editingUser && '(leave blank to keep)'}</label><input type="password" value={userForm.password} onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))} className="input" /></div>
              <div className="form-group"><label className="form-label required">Role</label><select value={userForm.role} onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value }))} className="select" required><option value="EVALUATION_PANEL_MEMBER">Evaluation Panel Member</option><option value="PROCESS_OWNER">Process Owner</option><option value="AUDITOR">Auditor/Probity Advisor</option><option value="ADMIN">Administrator</option><option value="SUPER_ADMIN">Super Administrator</option></select></div>
              <div className="form-group"><label className="form-label">Tenant</label><select value={userForm.tenant_id} onChange={(e) => setUserForm(prev => ({ ...prev, tenant_id: e.target.value }))} className="select"><option value="">No Tenant (System-wide)</option>{tenants.map((tenant) => (<option key={tenant.id} value={tenant.id}>{tenant.name}</option>))}</select></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label" style={{display: 'flex', alignItems: 'center'}}><input type="checkbox" checked={userForm.is_active} onChange={(e) => setUserForm(prev => ({ ...prev, is_active: e.target.checked }))} style={{ marginRight: '0.5rem', height: '1rem', width: '1rem' }}/> Active</label></div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" onClick={resetUserForm} className="btn btn-tertiary">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingUser ? 'Update User' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={styles.contentWrapper}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xl)' }}>
          <h1 style={styles.pageTitle}>Super Admin Dashboard</h1>
          <button onClick={onBack} className="btn btn-secondary">← Back to Dashboard</button>
        </div>

        <div>
          {/* Tab Navigation */}
          <div style={styles.tabContainer}>
            {[ { key: 'overview', label: 'System Overview' }, { key: 'tenants', label: 'Tenancy Management' }, { key: 'users', label: 'User Management' }, { key: 'audit', label: 'Audit Logs' } ].map((tab) => (
              <div key={tab.key} style={{ ...styles.tab, ...(activeTab === tab.key ? styles.activeTab : {}) }} onClick={() => setActiveTab(tab.key as any)}>
                {tab.label}
              </div>
            ))}
          </div>

          {/* System Overview Tab */}
          {activeTab === 'overview' && systemStats && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">System Overview</h2>
                <p className="card-subtitle">Monitor system-wide statistics and performance</p>
              </div>
              <div className="card-content">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--spacing-md)' }}>
                  <div style={{ background: 'var(--color-surface)', padding: 'var(--spacing-lg)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--color-surface-variant)', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-primary)' }}>🏢</div>
                    <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>{systemStats.active_tenants} / {systemStats.total_tenants}</div>
                    <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Active Tenants</div>
                  </div>
                  <div style={{ background: 'var(--color-surface)', padding: 'var(--spacing-lg)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--color-surface-variant)', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-success)' }}>👥</div>
                    <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>{systemStats.active_users} / {systemStats.total_users}</div>
                    <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Active Users</div>
                  </div>
                  <div style={{ background: 'var(--color-surface)', padding: 'var(--spacing-lg)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--color-surface-variant)', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-warning)' }}>📊</div>
                    <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>{systemStats.active_processes} / {systemStats.total_processes}</div>
                    <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Active Processes</div>
                  </div>
                  <div style={{ background: 'var(--color-surface)', padding: 'var(--spacing-lg)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--color-surface-variant)', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-info)' }}>🔄</div>
                    <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>{systemStats.recent_logins}</div>
                    <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Recent Logins (24h)</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tenancy Management Tab */}
          {activeTab === 'tenants' && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Tenancy Management</h2>
                <button onClick={() => { setEditingTenant(null); resetTenantForm(); setShowTenantModal(true); }} className="btn btn-primary">+ Create Tenant</button>
              </div>
              <div className="card-content">
                <div className="table-responsive">
                <table className="table">
                  <thead><tr><th>Organisation</th><th>Slug</th><th>Contact</th><th>Tier</th><th style={{ textAlign: 'center' }}>Users</th><th style={{ textAlign: 'center' }}>Processes</th><th style={{ textAlign: 'center' }}>Status</th><th style={{ textAlign: 'center' }}>Actions</th></tr></thead>
                  <tbody>
                    {tenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td><div style={{ fontWeight: 'bold' }}>{tenant.name}</div><div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Created: {new Date(tenant.created_at).toLocaleDateString('en-AU')}</div></td>
                        <td><code style={{ background: 'var(--color-surface-variant)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>{tenant.slug}</code></td>
                        <td>{tenant.contact_email || 'Not set'}</td>
                        <td><span className="badge" style={{ textTransform: 'capitalize', background: tenant.subscription_tier === 'enterprise' ? 'var(--color-primary)' : tenant.subscription_tier === 'premium' ? 'var(--color-warning)' : 'var(--color-success)', color: 'white' }}>{tenant.subscription_tier}</span></td>
                        <td style={{ textAlign: 'center' }}>{tenant.user_count}</td>
                        <td style={{ textAlign: 'center' }}>{tenant.process_count}</td>
                        <td style={{ textAlign: 'center' }}><span className="badge" style={{ background: tenant.is_active ? 'var(--color-success)' : 'var(--color-error)', color: 'white' }}>{tenant.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td className="table-actions">
                          <button onClick={() => handleEditTenant(tenant)} className="btn btn-secondary btn-sm" title="Edit tenant details">Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {tenants.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">🏢</div>
                    <h3 className="empty-state-title">No Tenants Found</h3>
                    <p className="empty-state-description">Click "Create Tenant" to add the first organisation.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User Management Tab */}
          {activeTab === 'users' && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Cross-Tenant User Management</h2>
                <button onClick={() => { setEditingUser(null); resetUserForm(); setShowUserModal(true); }} className="btn btn-primary">+ Create User</button>
              </div>
              <div className="card-content">
                {/* Tenancy Filter */}
                <div style={{ marginBottom: 'var(--spacing-lg)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)' }}>Filter by Tenancy:</label>
                  <select 
                    value={selectedTenantFilter} 
                    onChange={(e) => filterUsersByTenant(e.target.value)}
                    className="select"
                    style={{ minWidth: '200px' }}
                  >
                    <option value="all">All Users ({users.length})</option>
                    <option value="system">System-wide Only ({users.filter(u => !u.tenant_id).length})</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id.toString()}>
                        {tenant.name} ({users.filter(u => u.tenant_id === tenant.id).length})
                      </option>
                    ))}
                  </select>
                  <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-body-small)' }}>
                    Showing {filteredUsers.length} of {users.length} users
                  </div>
                </div>
                <div className="table-responsive">
                <table className="table">
                  <thead><tr><th>User</th><th>Role</th><th>Tenant</th><th style={{ textAlign: 'center' }}>Status</th><th style={{ textAlign: 'center' }}>Last Login</th><th style={{ textAlign: 'center' }}>Actions</th></tr></thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td><div style={{ fontWeight: 'bold' }}>{user.full_name}</div><div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>@{user.username} | {user.email}</div></td>
                        <td><span className="badge" style={{ textTransform: 'capitalize', background: user.role === 'SUPER_ADMIN' ? 'var(--color-error)' : user.role === 'ADMIN' ? 'var(--color-primary)' : user.role === 'PROCESS_OWNER' ? 'var(--color-warning)' : user.role === 'AUDITOR' ? 'var(--color-secondary)' : 'var(--color-success)', color: 'white' }}>{user.role.replace(/_/g, ' ')}</span></td>
                        <td>{user.tenant_name ? (<span style={{ fontWeight: '500' }}>{user.tenant_name}</span>) : (<span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>System-wide</span>)}</td>
                        <td style={{ textAlign: 'center' }}><span className="badge" style={{ background: user.is_active ? 'var(--color-success)' : 'var(--color-error)', color: 'white' }}>{user.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td style={{ textAlign: 'center', fontSize: '0.9rem' }}>{user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('en-AU') : 'Never'}</td>
                        <td className="table-actions"><div style={{ display: 'flex', gap: 'var(--spacing-xs)', justifyContent: 'center', flexWrap: 'wrap' }}><button onClick={() => handleEditUser(user)} className="btn btn-secondary btn-sm" title="Edit user details">Edit</button>{user.id !== currentUser?.id && (<button onClick={() => handleToggleUserActive(user)} className={`btn btn-sm ${user.is_active ? 'btn-destructive' : 'btn-success'}`} title={user.is_active ? 'Deactivate user' : 'Activate user'}>{user.is_active ? 'Disable' : 'Enable'}</button>)}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {filteredUsers.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">👥</div>
                    <h3 className="empty-state-title">No Users Found</h3>
                    <p className="empty-state-description">{selectedTenantFilter === 'all' ? 'No users exist in the system.' : 'No users found for the selected filter.'}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Logs Tab */}
          {activeTab === 'audit' && (
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">System Audit Logs</h2>
                <p className="card-subtitle">Track all administrative actions and system events</p>
              </div>
              <div className="card-content">
                <div className="table-responsive">
                <table className="table">
                  <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Resource</th><th>Details</th><th>IP Address</th></tr></thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td style={{ fontSize: '0.8rem' }}>{new Date(log.created_at).toLocaleString('en-AU')}</td>
                        <td>{log.username ? (<span style={{ fontWeight: '500' }}>@{log.username}</span>) : (<span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>System</span>)}</td>
                        <td><code style={{ background: 'var(--color-surface-variant)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.8rem' }}>{log.action}</code></td>
                        <td>{log.resource_type && (<span style={{ fontSize: '0.9rem' }}>{log.resource_type}{log.resource_id && ` #${log.resource_id}`}</span>)}</td>
                        <td style={{ fontSize: '0.9rem', maxWidth: '300px' }}>{log.details}</td>
                        <td style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{log.ip_address || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {auditLogs.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <h3 className="empty-state-title">No Audit Logs Found</h3>
                    <p className="empty-state-description">System audit logs will appear here as actions are performed.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
